import { Badge, Radio, Typography } from '@one-thd/sui-atomic-components';
import { ErrorFilled } from '@one-thd/sui-icons';
import React from 'react';
import { IPayment } from '../../models/payments';
import { getCardImagePath } from '../../utils/credit-cards';
import { useViews } from '../../hooks/use-views';
import { EDIT_PAYMENT } from '../../models/views';
import { usePayments } from '../../hooks/use-payments';

type IPaymentCardProps = {
  card: IPayment | undefined;
  selectedPaymentMethod: IPayment | undefined;
  index?: number;
  onSelect?: (card: IPayment | undefined) => void;
};

export const PaymentCard = ({
  card,
  index = 0,
  selectedPaymentMethod,
  onSelect,
}: IPaymentCardProps) => {
  const { goToView } = useViews();
  const { setSelectedPaymentMethod } = usePayments();
  const cardBrand = card?.cardDetails.cardBrand.name || '';
  const cardNumber = `****${card?.cardDetails.cardNumberLast4 || ''}`;
  const expirationDate =
    card?.cardDetails.expirationMonth &&
    card?.cardDetails.expirationYear &&
    `Exp ${
      card?.cardDetails.expirationMonth
    }/${card?.cardDetails.expirationYear?.slice(2)}`;
  const isCurrentlySelected = selectedPaymentMethod?.id === card?.id;
  const showUpdateMsg =
    isCurrentlySelected &&
    (card?.cardDetails?.isExpired || !card?.billingAddress?.id);
  const isPlccCard = ['HDCOM', 'PROX', 'PROP'].includes(cardBrand);

  const badgeColor: Record<string, string> = {
    inactive: 'medium',
    active: 'info',
    expired: 'danger',
  };

  const handleOnSelect = () => {
    if (onSelect) {
      onSelect(card);
    }
  };

  return (
    <div
      className={`sui-flex sui-items-baseline sui-mb-5 sui-w-full ${
        isPlccCard && 'sui-pointer-events-none'
      }`}
      onClick={handleOnSelect}
      role="button"
      tabIndex={0}
      data-testid={`radio-${index}`}
    >
      <Radio
        disabled={isPlccCard}
        checked={selectedPaymentMethod?.id === card?.id}
        value={index}
        name="radio-buttons"
        inputAttributes={{
          'aria-label': `Card ${card?.nickName}`,
        }}
      />
      <div className="sui-w-full">
        <div className={`${isPlccCard && 'sui-opacity-50'}`}>
          <div className="sui-flex sui-mb-1">
            <img
              width={25}
              height="auto"
              className="sui-object-contain sui-inline-block sui-mr-1"
              src={getCardImagePath(cardBrand)}
              alt="Credit card logo"
            />
            <div className="sui-mr-4">
              <Typography data-qm-mask="">{cardNumber}</Typography>
            </div>
            {expirationDate && <Typography>{expirationDate}</Typography>}
          </div>
          <div className="sui-mb-1">
            <Typography>{card?.nickName}</Typography>
          </div>
          {card?.isDefault && (
            <span className="sui-mr-1">
              <Badge variant="strong" color={badgeColor.inactive} size="small">
                Default
              </Badge>
            </span>
          )}
          {card?.cardDetails?.isExpired && (
            <Badge variant="subtle" color={badgeColor.expired} size="small">
              Expired
            </Badge>
          )}
        </div>
        {showUpdateMsg && (
          <div className="sui-flex sui-gap-2 sui-items-baseline sui-mt-1">
            <div className="sui-w-fit">
              <ErrorFilled size="small" color="danger" />
            </div>
            <Typography color="danger">
              Missing or invalid card details.{' '}
              <Typography
                component="button"
                decoration="underline"
                color="danger"
                onClick={() => {
                  setSelectedPaymentMethod(card);
                  goToView(EDIT_PAYMENT);
                }}
              >
                Update your information
              </Typography>{' '}
              to finish selection.
            </Typography>
          </div>
        )}
        {isPlccCard && (
          <div className="sui-flex sui-gap-2 sui-items-baseline sui-mt-1">
            <div className="sui-w-fit">
              <ErrorFilled size="small" color="danger" />
            </div>
            <Typography color="danger">
              Sorry, we currently don&apos;t support Home Depot Commercial
              Account for Instant Checkout.
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCard;
